<script>
import { useRoute } from "vue-router";
import { chatApi, chatsApi, talksApi } from "@/api";
import LoadingIcon from "@/components/LoadingIcon.vue";
import { ChatSet, ChatMeta } from "@/store/modules/chat";
import { TalksReferences, References } from "@/store/modules/talks";

export default {
  components: {
    LoadingIcon,
  },
  data() {
    const route = useRoute();
    const chatId = parseInt(route.params.chat_id);
    return {
      chatId,
      history_chat: [],
      chatSet: new ChatSet(),
      show_messages: false,
      modalTitle: "",
      metaModalText: "",
      resourceId: "",
      userMessage: "",
      filterVisible: false, // フィルターセクションの表示・非表示制御
      isFilterSectionVisible: true,
      isLoading: false,
      isMetaModalVisible: false,
      showModal: false,
      tags: [],
      file_name: "",
      keyDownCode: 0,
      input_msg: this.$t('ChatScreenView.input_msg'),
      errorMessage: this.$t('ChatScreenView.error_messages'),
      close: this.$t('ChatScreenView.close'),
      showTemplate: true,
      chatQuestionTemplateList: [],
      talkList: [],
      showTalkList: true,
      selectedTalkId: 0,
      page: 2,
      is_bottom: false,
      newtalkTitle: "New Talk",
      selectedTalkTitle: "New Talk",
      ws: undefined,
      timer: undefined,
      memoryFlag: true,
      templateFlag: true,
      thinkingFlag: false,
      ai_model: "",
      currentModelName: "",
      showTemplateSearch: true,
      pinnedTalkIds: {},
      pinnedOrder: [], // ピン留めされた会話の順番を管理
      aiModelsList: [], // 所有 AI 模型列表
      gpt5ModelId: null, // gpt-5 模型 ID
      gpt5ThinkingModelId: null, // gpt-5-thinking 模型 ID
      isUpdatingModel: false, // 防止循環更新的標誌
      notFoundReplies: ["見つかりません", "見つかりません。", "找不到", "找不到。"],
      skipWsNextMessage: false,
      pendingClarificationContext: null,
    };
  },
  created: function () {
    return this.$store.dispatch("viewMe");
  },
  watch: {
    async thinkingFlag(newVal, oldVal) {
      // 防止初始化時的觸發
      if (oldVal === undefined) return;
      // 防止循環更新
      if (this.isUpdatingModel) return;
      
      // 只有當模型是 gpt-5 或 gpt-5-thinking 時才處理
      if (this.currentModelName !== 'gpt-5' && this.currentModelName !== 'gpt-5-thinking') {
        return;
      }
      
      try {
        this.isUpdatingModel = true;
        
        if (newVal) {
          // 開啟 thinking mode：如果當前是 gpt-5，切換到 gpt-5-thinking
          if (this.currentModelName === 'gpt-5' && this.gpt5ThinkingModelId) {
            await this.updateChatModel(this.gpt5ThinkingModelId);
            this.currentModelName = 'gpt-5-thinking';
            console.log('Thinking mode enabled: switched to gpt-5-thinking');
          }
        } else {
          // 關閉 thinking mode：如果當前是 gpt-5-thinking，切換到 gpt-5
          if (this.currentModelName === 'gpt-5-thinking' && this.gpt5ModelId) {
            await this.updateChatModel(this.gpt5ModelId);
            this.currentModelName = 'gpt-5';
            console.log('Thinking mode disabled: switched to gpt-5');
          }
        }
      } catch (error) {
        console.error('Error updating model based on thinking mode:', error);
        // 恢復原來的狀態
        this.thinkingFlag = oldVal;
      } finally {
        this.isUpdatingModel = false;
      }
    },
    async currentModelName(newVal, oldVal) {
      // 防止初始化時的觸發
      if (oldVal === undefined || oldVal === '') return;
      // 防止循環更新
      if (this.isUpdatingModel) return;
      
      // 當模型改變時，自動調整 thinking mode
      if (newVal === 'gpt-5-thinking') {
        // 如果切換到 gpt-5-thinking，自動開啟 thinking mode
        if (!this.thinkingFlag) {
          this.isUpdatingModel = true;
          this.thinkingFlag = true;
          this.isUpdatingModel = false;
          console.log('Model changed to gpt-5-thinking, thinking mode automatically enabled');
        }
      } else if (newVal === 'gpt-5') {
        // 如果切換到 gpt-5，關閉 thinking mode（如果之前是 gpt-5-thinking）
        if (oldVal === 'gpt-5-thinking' && this.thinkingFlag) {
          this.isUpdatingModel = true;
          this.thinkingFlag = false;
          this.isUpdatingModel = false;
          console.log('Model changed from gpt-5-thinking to gpt-5, thinking mode disabled');
        }
      }
      
      // 通知父組件更新標籤（通過 window 自定義事件）
      window.dispatchEvent(new CustomEvent('model-changed', { detail: newVal }));
    },
  },
  computed: {
    history_chats() {
      const chatId = this.chatId;
      console.log(this.$store.getters.getChatById(chatId)?.chatSets || [])
      return this.$store.getters.getChatById(chatId)?.chatSets || [];
    },
    showCopyButton() {
      return this.history_chats.length > 0;
    },
    sortedTalkList() {
      // ピン留めされた会話を順番通りに、その後に通常の会話を表示
      const pinned = this.pinnedOrder.map(id => this.talkList.find(talk => talk.id === id)).filter(Boolean);
      const unpinned = this.talkList.filter(talk => !this.pinnedTalkIds[talk.id]);
      return [...pinned, ...unpinned];
    },
    showThinkingMode() {
      const modelName = this.currentModelName.toLowerCase();
      return modelName === "gpt-5" || modelName === "gpt-5-thinking";
    },
  },
  async mounted() {
    await this.$store.dispatch("createChatIfNotExist", this.chatId)
    this.setTemplate();
    this.setTalkList();
    this.clearMessages();
    
    // 獲取所有 AI 模型列表
    try {
      const modelsResp = await chatsApi.getAiModels();
      this.aiModelsList = modelsResp.data || [];
      
      // 找到 gpt-5 和 gpt-5-thinking 的模型 ID
      const gpt5Model = this.aiModelsList.find(m => m.model_name === 'gpt-5');
      const gpt5ThinkingModel = this.aiModelsList.find(m => m.model_name === 'gpt-5-thinking');
      
      if (gpt5Model) {
        this.gpt5ModelId = gpt5Model.id;
      }
      if (gpt5ThinkingModel) {
        this.gpt5ThinkingModelId = gpt5ThinkingModel.id;
      }
    } catch (error) {
      console.error('Error fetching AI models:', error);
    }
    
    const chat_res = await chatsApi.getById(this.chatId);
    const ai_model_res = await chatsApi.getAiModelName(chat_res.data.ai_model_id);
    this.ai_model = ai_model_res.data;
    const modelName = typeof ai_model_res.data === 'string' ? ai_model_res.data : (ai_model_res.data?.model_name || '');
    this.currentModelName = modelName;

    // 如果當前模型是 gpt-5-thinking，自動開啟 thinking mode
    if (modelName === 'gpt-5-thinking') {
      this.thinkingFlag = true;
      console.log('Model is gpt-5-thinking, thinking mode automatically enabled');
    }

    // リソース種別がRAGの場合は、テンプレート検索を無効にする
    // 旧データで resource_type が空の場合も RAG とみなす
    if (!chat_res.data.resource_type || chat_res.data.resource_type === "resource") {
      this.showTemplateSearch = false;
      this.templateFlag = false;
    }
  },
  beforeUnmount() {
    if (this.tagify) {
      this.tagify.destroy();
    }
    if (this.ws) {
      this.ws.close();
    }
  },
  methods: {
    getVisibleReferences(metaList = []) {
      return Array.isArray(metaList) ? metaList.slice(0, 3) : [];
    },
    resetPendingClarification() {
      this.pendingClarificationContext = null;
    },
    escapeHtml(text) {
      return String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    renderMessageLine(line) {
      const escaped = this.escapeHtml(line);
      return escaped.replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
      );
    },
    getMetaLabel(metadata = {}) {
      const fileName = (metadata.file_name || "").trim();
      if (fileName) {
        return fileName;
      }

      const title = (metadata.title || "").trim();
      if (title) {
        return title;
      }

      const datasetLabels = {
        ec_faq: "EC FAQ",
        store_faq: "Store FAQ",
        ec_guide: "EC Guide",
        ec_agreement: "EC Agreement",
        store_profile: "Store Profile",
      };
      const dataset = metadata.dataset || "";
      if (datasetLabels[dataset]) {
        return datasetLabels[dataset];
      }

      const documentUrl = (metadata.document_url || "").trim();
      if (documentUrl) {
        try {
          return new URL(documentUrl).hostname.replace(/^www\./, "");
        } catch (error) {
          return documentUrl;
        }
      }

      return this.$t('ChatScreenView.reference_source');
    },
    shouldUsePendingClarification(message) {
      if (!this.pendingClarificationContext) {
        return false;
      }
      const trimmed = (message || "").trim();
      if (!trimmed) {
        return false;
      }
      const candidates = this.pendingClarificationContext?.candidates || [];
      if (candidates.includes(trimmed)) {
        return true;
      }
      const detailKeywords = ["営業時間", "住所", "アクセス", "電話", "定休日", "營業時間", "地址"];
      return trimmed.length <= 20 && !detailKeywords.some((keyword) => trimmed.includes(keyword));
    },
    async selectClarificationCandidate(clarificationContext, candidate) {
      this.pendingClarificationContext = clarificationContext || null;
      this.userMessage = candidate;
      await this.sendMessage();
    },
    async togglePin(talkId) {
      const wasPinned = this.pinnedTalkIds[talkId];
      this.pinnedTalkIds[talkId] = !wasPinned;
      
      if (!wasPinned) {
        // ピン留めする場合、順番の最後に追加
        this.pinnedOrder.push(talkId);
      } else {
        // ピン留めを解除する場合、順番から削除
        const index = this.pinnedOrder.indexOf(talkId);
        if (index > -1) {
          this.pinnedOrder.splice(index, 1);
        }
      }
      
      // バックエンドに順番を保存
      await this.savePinnedOrder();
    },

    async savePinnedOrder() {
      try {
        // 現在のユーザー固有のピン留め順番を保存
        const talks = this.pinnedOrder.map((talkId, index) => ({
          id: talkId,
          pinned_order: index + 1
        }));
        
        // ピン留めを解除された会話も含めて更新（pinned_orderをnullに設定）
        const unpinnedTalks = this.talkList
          .filter(talk => !this.pinnedTalkIds[talk.id] && talk.pinned_order !== null)
          .map(talk => ({
            id: talk.id,
            pinned_order: null
          }));
        
        const allTalks = [...talks, ...unpinnedTalks];
        
        if (allTalks.length > 0) {
          // バックエンドは認証情報から現在のユーザーを取得し、UserTalkPinsテーブルに保存
          await talksApi.updateTalksPinnedOrder(this.chatId, allTalks);
        }
      } catch (error) {
        console.error('ピン留め順番の保存に失敗しました:', error);
      }
    },

    // ドラッグ&ドロップでピン留め会話の順番を変更
    onDragStart(event, talkId) {
      event.dataTransfer.setData('text/plain', talkId);
      event.dataTransfer.effectAllowed = 'move';
    },

    onDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },

    onDrop(event, targetTalkId) {
      event.preventDefault();
      const draggedTalkId = parseInt(event.dataTransfer.getData('text/plain'));
      
      if (draggedTalkId === targetTalkId) return;
      
      // ピン留めされた会話のみ順番を変更
      if (this.pinnedTalkIds[draggedTalkId] && this.pinnedTalkIds[targetTalkId]) {
        const draggedIndex = this.pinnedOrder.indexOf(draggedTalkId);
        const targetIndex = this.pinnedOrder.indexOf(targetTalkId);
        
        if (draggedIndex !== -1 && targetIndex !== -1) {
          // 配列から削除してから新しい位置に挿入
          this.pinnedOrder.splice(draggedIndex, 1);
          this.pinnedOrder.splice(targetIndex, 0, draggedTalkId);
          
          // バックエンドに順番を保存
          this.savePinnedOrder();
        }
      }
    },
    closeModal() {
      this.showModal = false;
      this.errorMessage = "";
    },
    keyDownEnter(e) {
      if (e.shiftKey) {
        // Shiftキーが同時に押されている場合は、デフォルトの動作（改行）を許可
        return;
      }
      this.keyDownCode = e.keyCode; // enterを押した時のkeycodeを記録
      e.preventDefault();
    },
    keyUpEnter(e) {
      if (this.keyDownCode === 229) { // 229コードの場合は処理をストップ
        return;
      }
      e.preventDefault();
      this.sendMessage();
    },
    keyEnterShift() {
      // shift+enterの場合はデフォルトの動作を許可して改行する
      console.log('shift,enter')
    },
    async createWebSocketConnection() {
      const wsId = Math.random().toString(32).substring(2)

      // WebSocket URLの構築と変換
      let wsUrl = `${process.env.VUE_APP_WEBSOCKET_URL}/ws/${wsId}`;

      console.log('Environment variable VUE_APP_WEBSOCKET_URL:', process.env.VUE_APP_WEBSOCKET_URL);
      console.log('Initial WebSocket URL:', wsUrl);

      // HTTPSをWSSに、HTTPをWSに変換
      if (wsUrl.startsWith('https://')) {
        wsUrl = wsUrl.replace('https://', 'wss://');
        console.log('Converted HTTPS to WSS:', wsUrl);
      } else if (wsUrl.startsWith('http://')) {
        wsUrl = wsUrl.replace('http://', 'ws://');
        console.log('Converted HTTP to WS:', wsUrl);
      } else if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
        console.error('Invalid WebSocket URL protocol:', wsUrl);
        this.isLoading = false;
        this.showModal = true;
        this.errorMessage = `無効なWebSocket URL: ${wsUrl}`;
        return wsId;
      }

      console.log('Final WebSocket URL:', wsUrl);

      try {
        const ws = new WebSocket(wsUrl);

        ws.onopen = (event) => {
          console.log('WebSocket connection opened successfully:', event);
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          console.error('WebSocket URL used:', wsUrl);
          this.isLoading = false;
          this.showModal = true;
          this.errorMessage = `WebSocket接続エラー: ${wsUrl}`;
        };

        ws.onclose = (event) => {
          console.log('WebSocket connection closed:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
        };

        ws.onmessage = (event) => {
          (async () => {
            try {
              if (this.skipWsNextMessage) {
                this.skipWsNextMessage = false;
                ws.close();
                return;
              }
              console.log('WebSocket message received:', event);
              const wsResponseData = JSON.parse(event.data);
              console.log('Parsed WebSocket data:', wsResponseData);
              if (wsResponseData.error) {
                this.isLoading = false;
                this.showModal = true;
                this.show_messages = false;
              } else {
                clearTimeout(this.timer);
                await this.processResponse(wsResponseData);
              }
            } catch (error) {
              console.error('Error processing WebSocket message:', error);
              this.isLoading = false;
              this.showModal = true;
              this.show_messages = false;
            } finally {
              clearTimeout(this.timer);
              ws.close();
            }
          })();
        };

        // WebSocketインスタンスを保存
        this.ws = ws;

        return wsId;
      } catch (error) {
        console.error('Failed to create WebSocket connection:', error);
        this.isLoading = false;
        this.showModal = true;
        this.errorMessage = `WebSocket作成エラー: ${error.message}`;
        return wsId;
      }
    },
    async processStreamResponse(responseChunks, chunks = [], retrievalCompleted = false) {
      const normalizedChunks = Array.isArray(responseChunks)
        ? responseChunks
        : (responseChunks && Array.isArray(responseChunks.results) ? [responseChunks] : []);

      // チャットセットの初期化
      this.chatSet.question = this.userMessage;
      this.chatSet.messages = [];
      this.chatSet.meta = [];
      this.chatSet.debugChunks = [];
      this.chatSet.clarificationCandidates = [];
      this.chatSet.clarificationContext = null;
      this.chatSet.created_at = new Date().toISOString();
      this.chatSet.question_created_at = new Date().toISOString();
      this.chatSet.response_created_at = null;
      this.show_messages = true;

      // 参照データがある場合（templateFlag=Falseの場合）のみ処理
      if (normalizedChunks.length > 0) {
        this.applyQueryResultsToChatSet(normalizedChunks);
      }

      // ストリームリクエスト
      let msgResponse;
      try {
        msgResponse = await chatApi.chat_stream(
          chunks, // 文字列の配列として渡す
          this.userMessage,
          this.chatId,
          this.selectedTalkId,
          this.memoryFlag,
          this.templateFlag,
          this.ai_model,
          this.thinkingFlag,
          retrievalCompleted,
          this.shouldUsePendingClarification(this.userMessage) ? this.pendingClarificationContext : null
        )
      } catch (error) {
        console.error('Error calling chat_stream API:', error);
        this.isLoading = false;
        this.isProcessingMessage = false;
        this.showModal = true;
        this.errorMessage = `ストリーミングリクエストが失敗しました: ${error.message || 'Network Error'}`;
        this.show_messages = false;
        clearTimeout(this.timer);
        return;
      }

      console.log('msgResponse:', msgResponse);
      console.log('msgResponse status:', msgResponse?.status);

      if (!msgResponse || msgResponse.status !== 200) {
        console.error('Stream request failed:', msgResponse);
        this.isLoading = false;
        this.isProcessingMessage = false;
        this.showModal = true;
        this.errorMessage = `ストリーミングリクエストが失敗しました。ステータス: ${msgResponse?.status || 'undefined'}`;
        this.show_messages = false;
        clearTimeout(this.timer);
        return;
      }

      let streamResponseData = {};
      try {
        streamResponseData = msgResponse.body.getReader();
        console.log('Stream reader obtained successfully');
      } catch (error) {
        console.error('Failed to get stream reader:', error);
        this.isLoading = false;
        this.showModal = true;
        this.errorMessage = `ストリームリーダーの取得に失敗しました: ${error.message}`;
        this.show_messages = false;
        return
      }

      // ストリームレスポンスのパース処理
      let result = true;
      let chat_line = "";
      let i = 0;
      let isFirstResponse = true; // 最初のレスポンスかどうかを追跡
      this.chatSet.messages.push("");
      
      try {
      while (result) {
        let readResult;
        try {
          readResult = await streamResponseData.read();
        } catch (readError) {
          // ネットワークエラーやストリーム読み取りエラーをキャッチ
          console.error('Error reading from stream:', readError);
          // 既に受信した内容があれば表示
          if (chat_line || this.chatSet.messages.some(msg => msg && msg.trim())) {
            this.chatSet.response_created_at = new Date().toISOString();
            this.isLoading = false;
            this.isProcessingMessage = false;
            console.log('Stream interrupted, but partial content received');
            return;
          }
          // 内容が全くない場合はエラーを表示
          throw new Error(`ストリーム読み取りエラー: ${readError.message || 'network error'}`);
        }

        const { done, value } = readResult;

        // 最初のレスポンスを受信した時点でローディングをオフにする
          if (isFirstResponse && !done && value) {
          this.isLoading = false;
            this.isProcessingMessage = false;
          isFirstResponse = false;
          console.log('Stream started, loading turned off');
        }

        if (done) {
          console.log('Stream ended');
          result = false;
          // ストリーム完了時に回答の日時を設定
          this.chatSet.response_created_at = new Date().toISOString();
          break;
        }

          if (!value) {
            console.warn('Received empty value from stream, continuing...');
            continue;
          }

          this.show_messages = true;
        let result_string = new TextDecoder().decode(value);
          
        if (result_string.includes('\n')) {
          const normalizedLines = result_string.replace(/\n\n+/g, "\n").split('\n');
          const firstLine = normalizedLines.shift() || "";
          chat_line += firstLine;
          this.chatSet.messages[i] = chat_line;

          for (const line of normalizedLines) {
            this.chatSet.messages.push("");
            i += 1;
            chat_line = line;
            this.chatSet.messages[i] = chat_line;
          }
        } else {
          chat_line += result_string;
          this.chatSet.messages[i] = chat_line;
        }
      }
      } catch (error) {
        console.error('Error processing stream:', error);
        this.isLoading = false;
        this.isProcessingMessage = false;
        this.showModal = true;
        // エラーメッセージをより詳細に
        const errorMsg = error.message || 'Unknown error';
        this.errorMessage = `ストリーム処理エラー: ${errorMsg}`;
        this.show_messages = false;
        clearTimeout(this.timer);
        return;
      }

      // 保持模型原始回覆，避免前端強制覆寫造成錯答

      // 流式響應完成後，重置狀態以允許繼續提問
      this.isLoading = false;
      this.isProcessingMessage = false;

      // Chatインスタンスに格納
      const chatSet = new ChatSet();
      chatSet.messages = this.chatSet.messages;
      chatSet.question = this.chatSet.question;
      chatSet.meta = this.chatSet.meta;
      chatSet.debugChunks = this.chatSet.debugChunks;
      chatSet.clarificationCandidates = this.chatSet.clarificationCandidates;
      chatSet.clarificationContext = this.chatSet.clarificationContext;
      chatSet.created_at = this.chatSet.created_at;
      chatSet.response_created_at = this.chatSet.response_created_at;

      // メッセージ履歴storeにpush
      console.log(chatSet)
      try {
        this.$store.commit("updateChatMessages", { chatSet: chatSet, chatId: this.chatId });
      } catch (error) {
        console.log(error);
      }

      // 会話履歴保存
      await this.seveConversations(this.chatSet);

      // チャット情報収納変数初期化
      this.chatSet.meta = [];
      this.chatSet.debugChunks = [];
      this.chatSet.clarificationCandidates = [];
      this.chatSet.clarificationContext = null;
      this.userMessage = "";
      this.filterVisible = false;
      this.resetPendingClarification();
      
      // 清空 chatSet 以防止重複顯示
      this.chatSet.messages = [];
      this.chatSet.question = "";
      
      // 重置 show_messages 以允許繼續提問
      this.show_messages = false;

      //  DOM適用が完了したら一番下までスクロール
      this.$nextTick(() => {
        const container = this.$refs.messageHistory;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });

      // テンプレート検索後
      // テンプレート検索有効=falseに変える。
      if (this.templateFlag) {
        this.templateFlag = false;
      }

      // タイマーをクリア
      clearTimeout(this.timer);
    },
    applyQueryResultsToChatSet(normalizedChunks) {
      this.chatSet.meta = [];
      this.chatSet.debugChunks = [];
      if (!normalizedChunks.length) {
        return;
      }

      const res_query_results = normalizedChunks.flatMap((qr) => qr.results || []);
      for (const result_row of res_query_results) {
        let res_chat_set_meta = new ChatMeta();
        res_chat_set_meta.file_name = this.getMetaLabel(result_row.metadata);
        res_chat_set_meta.document_url = result_row.metadata.document_url;
        res_chat_set_meta.tags = `タグ：${result_row.metadata.tags}`;
        res_chat_set_meta.resource_id = result_row.metadata.resource_id;
        res_chat_set_meta.text = result_row.text;
        this.chatSet.meta.push(res_chat_set_meta);
      }
      this.chatSet.debugChunks = res_query_results
        .map((row) => row.text || "")
        .filter((text) => text.trim() !== "");
    },
    async processClarificationResponse(responseData) {
      const rawQueryResults = responseData.query_results;
      const normalizedChunks = Array.isArray(rawQueryResults)
        ? rawQueryResults
        : (rawQueryResults && Array.isArray(rawQueryResults.results) ? [rawQueryResults] : []);

      this.chatSet.question = this.userMessage;
      this.chatSet.messages = [responseData.clarification || ""];
      this.chatSet.created_at = new Date().toISOString();
      this.chatSet.question_created_at = new Date().toISOString();
      this.chatSet.response_created_at = new Date().toISOString();
      this.chatSet.clarificationCandidates = responseData.candidates || [];
      this.chatSet.clarificationContext = responseData.clarification_context || null;
      this.show_messages = true;
      this.isLoading = false;
      this.isProcessingMessage = false;
      this.pendingClarificationContext = responseData.clarification_context || null;
      this.applyQueryResultsToChatSet(normalizedChunks);

      const chatSet = new ChatSet();
      chatSet.messages = this.chatSet.messages;
      chatSet.question = this.chatSet.question;
      chatSet.meta = this.chatSet.meta;
      chatSet.debugChunks = this.chatSet.debugChunks;
      chatSet.clarificationCandidates = this.chatSet.clarificationCandidates;
      chatSet.clarificationContext = this.chatSet.clarificationContext;
      chatSet.created_at = this.chatSet.created_at;
      chatSet.response_created_at = this.chatSet.response_created_at;

      this.$store.commit("updateChatMessages", { chatSet: chatSet, chatId: this.chatId });
      await this.seveConversations(this.chatSet);

      this.chatSet.meta = [];
      this.chatSet.debugChunks = [];
      this.chatSet.clarificationCandidates = [];
      this.chatSet.clarificationContext = null;
      this.userMessage = "";
      this.filterVisible = false;
      this.chatSet.messages = [];
      this.chatSet.question = "";
      this.show_messages = false;
      clearTimeout(this.timer);
    },
    async processResponse(responseData) {
      if (responseData?.response_type === "clarification" && responseData?.clarification) {
        await this.processClarificationResponse(responseData);
        return;
      }

      // 参照リソース表示用のチャンクを取得
      const rawQueryResults = responseData.query_results;
      const res_query_results = Array.isArray(rawQueryResults)
        ? rawQueryResults
        : (rawQueryResults && Array.isArray(rawQueryResults.results) ? [rawQueryResults] : []);

      // chunksデータを正しい形式（文字列の配列）に変換
      let chunks = [];
      if (res_query_results && res_query_results.length > 0) {
        // resultsの中身を文字列として抽出
        const results = res_query_results.flatMap((qr) => qr.results || []);
        chunks = results.map(result => result.text || '').filter(text => text.trim() !== '');
      }

      console.log('Extracted chunks:', chunks);
      await this.processStreamResponse(res_query_results, chunks, true);
    },
    async sendMessage() {
      console.log('senMessage');
      //入力がない場合は実行しない
      if (this.userMessage === "") {
        return
      }

      // ローディング開始
      this.isLoading = true;

      // 根據模型類型設置不同的超時時間
      // GPT-5-thinking mode 需要更長的時間（最多 15 分鐘 = 900 秒，與 Nginx 和後端一致）
      // GPT-5 普通模式 3 分鐘（180 秒）
      // Grok 模型 5 分鐘（300 秒，與後端超時時間一致）
      // 普通模式 2 分鐘（120 秒）
      const timeoutDuration = (this.thinkingFlag && (this.currentModelName === 'gpt-5' || this.currentModelName === 'gpt-5-thinking')) 
        ? 900000  // 15 分鐘（thinking mode，與 Nginx 超時時間一致）
        : (this.currentModelName === 'gpt-5' || this.currentModelName === 'gpt-5-thinking')
          ? 180000  // 3 分鐘（GPT-5 普通模式）
          : (this.currentModelName && this.currentModelName.toLowerCase().includes('grok'))
            ? 300000  // 5 分鐘（Grok 模型，與後端超時時間一致）
            : 120000; // 2 分鐘（普通模式）
      
      // 設置超時計時器
      this.timer = setTimeout(this.showTimeOutModal, timeoutDuration);

      // resource_type=resourceの場合は常にWebSocketと参照データを取得するため、templateFlagをfalseに設定
      if (!this.showTemplateSearch) {
        this.templateFlag = false;
      }

      // templateFlag=Trueの場合は直接ストリームリクエストを実行
      if (this.templateFlag) {
        console.log('Template mode: Skipping WebSocket and reference data, directly calling stream');
        await this.processStreamResponse([], [], false); // responseChunks=[], chunks=[]
        return;
      }

      // templateFlag=Falseの場合は従来の処理（WebSocket + 参照データ取得）
      const wsId = await this.createWebSocketConnection();
      const clarificationContext = this.shouldUsePendingClarification(this.userMessage)
        ? this.pendingClarificationContext
        : null;
      if (!clarificationContext) {
        this.resetPendingClarification();
      }

      // query request (request vectordb, return chunks)
      // metadata
      const metadata = {};

      const queries = [{ query: this.userMessage, top_k: 5, filter: metadata }]
      try {
        const response = await chatApi.chat_reference(
          queries,
          this.userMessage,
          this.chatId,
          wsId,
          clarificationContext
        )
        if (response.status != 200) {
          throw new Error("ERROR");
        }
        if (response?.data?.query_results) {
          this.skipWsNextMessage = true;
          if (this.ws) {
            this.ws.close();
          }
          await this.processResponse(response.data);
          return;
        }
      } catch (error) {
        console.error('chat_reference failed, fallback to direct stream:', error);
        // 参照取得に失敗した場合は、参照なしでストリームを続行
        await this.processStreamResponse([], [], false);
        return;
      }
    },

    setMetaMsg(resource_id, modalTitle, metaModalText) {
      this.resourceId = resource_id;
      this.modalTitle = modalTitle
      this.metaModalText = metaModalText
      this.isMetaModalVisible = true;
    },

    toggleFilterSection() {
      this.isFilterSectionVisible = !this.isFilterSectionVisible;
    },

    // モーダルを表示するメソッド
    showMetaModal() {
      this.isMetaModalVisible = true;
    },

    // モーダルを非表示にするメソッド
    closeMetaModal() {
      this.isMetaModalVisiblee = false;
    },

    clearMessages() {
      this.messages = [],
        this.$store.commit("clearMessages", this.chatId)
    },

    async setTemplate() {
      await chatsApi.getTemplateById(this.chatId).then(response => {
        response.data.forEach(ChatQuestionTemplate => {
          this.chatQuestionTemplateList.push(ChatQuestionTemplate);
        });
      }).catch(() => {
        this.showTemplate = false;
      });
    },

    sendTemplate(question_text) {
      // 現在の入力内容に追記
      if (this.userMessage) {
        this.userMessage += '\n' + question_text;
      } else {
        this.userMessage = question_text;
      }
      // this.sendMessage();
    },

    showHistoryBar() {
      this.showTalkList = !this.showTalkList;
    },

    async handleScroll(event) {
      // 获取滚动容器的高度、滚动高度和视口高度
      // スクロールコンテナの高さ、スクロールの高さ、ビューポートの高さを取得する
      const container = event.target;
      const scrollHeight = container.scrollHeight;
      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;

      // 判断是否滚动到底部
      // 最下部までスクロールするかどうかを判断する
      if (scrollHeight - scrollTop === clientHeight) {
        // 处理滚动到底部的逻辑
        // 最下部までの場合
        console.log('Scrolled to the bottom!');
        this.is_bottom = true;
        try {
          const res = await talksApi.getTalks(this.chatId, this.page);
          if (res.data.length) {
            this.talkList.push.apply(this.talkList, res.data);
            this.page++;
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.is_bottom = false;
        }
      }
    },

    async setTalkList() {
      try {
        const res = await talksApi.getTalks(this.chatId, 1);
        if (res.data.length) {
          this.talkList = res.data;
          // ピン留めされた会話の情報を復元
          this.restorePinnedState();
        }
      } catch (error) {
        console.error(error);
      }
    },

    restorePinnedState() {
      // ユーザー固有のpinned_orderに基づいてピン留め状態を復元
      // バックエンドから取得したpinned_orderは現在のユーザーに固有の値
      this.pinnedTalkIds = {};
      this.pinnedOrder = [];
      
      this.talkList.forEach(talk => {
        if (talk.pinned_order !== null && talk.pinned_order !== undefined) {
          this.pinnedTalkIds[talk.id] = true;
          this.pinnedOrder.push(talk.id);
        }
      });
      
      // pinned_orderでソート
      this.pinnedOrder.sort((a, b) => {
        const talkA = this.talkList.find(t => t.id === a);
        const talkB = this.talkList.find(t => t.id === b);
        return (talkA?.pinned_order || 0) - (talkB?.pinned_order || 0);
      });
    },

    getTimeString(dateString) {
      let date = new Date(dateString)
      var y = date.getFullYear();
      var M = date.getMonth() + 1;
      var d = date.getDate();
      var H = date.getHours();
      var m = date.getMinutes();
      var s = date.getSeconds();
      return y + '-' + (M < 10 ? ('0' + M) : M) + '-'
        + (d < 10 ? ('0' + d) : d) + " " + (H < 10 ? ('0' + H) : H)
        + ":" + (m < 10 ? ('0' + m) : m) + ":" + (s < 10 ? ('0' + s) : s);
    },

    getMessageTimeString(dateString) {
      let date = new Date(dateString)
      var M = date.getMonth() + 1;
      var d = date.getDate();
      var H = date.getHours();
      var m = date.getMinutes();
      return (M < 10 ? ('0' + M) : M) + '/' + (d < 10 ? ('0' + d) : d) + " " + (H < 10 ? ('0' + H) : H) + ":" + (m < 10 ? ('0' + m) : m);
    },

    async createNewTalk(title) {
      try {
        this.selectedTalkTitle = title;
        if (this.selectedTalkId != 0) {
          this.clearMessages();
        }
        const res = await talksApi.createTalk(this.chatId, title);
        this.talkList.unshift(res.data);
        this.selectedTalkId = res.data.id;
        // 新しいトークを作成したらテンプレート検索を有効にする
        this.templateFlag = true;
      } catch (error) {
        console.error(error);
      }
    },

    handleTalkClick(talkId, index) {
      // ストリームメッセージのレスポンス中は会話切り替えを無効にする
      if (this.show_messages) {
        console.log('Cannot switch conversations during streaming response');
        return;
      }
      this.getConversations(talkId, index);
    },

    async getConversations(talkId, index) {
      this.selectedTalkTitle = this.talkList[index].talk_title;
      this.showModal = true;
      this.clearMessages();
      this.selectedTalkId = talkId;
      this.history_chat = [];
      this.chatSet = new ChatSet();
      try {
        const res = await talksApi.getConversations(talkId);
        if (res.data.length) {
          res.data.forEach((item) => {
            const chatSet = new ChatSet();
            chatSet.question = item.request_message;
            chatSet.messages = item.response_message.split("\n");
            chatSet.created_at = item.created_at || new Date().toISOString();
            chatSet.question_created_at = item.request_created_at || item.created_at || new Date().toISOString();
            chatSet.response_created_at = item.response_created_at || item.created_at || new Date().toISOString();
            chatSet.meta = item.talk_references.map((data) => {
              const meta = new ChatMeta()
              meta.document_url = data.document_url;
              meta.file_name = data.file_name;
              meta.resource_id = data.resource_id;
              meta.text = data.text;
              return meta
            });
            this.$store.commit("updateChatMessages", { chatSet: chatSet, chatId: this.chatId });
          });
          this.history_chat.push(res.data);
        }
        this.showModal = false;
      } catch (error) {
        console.error(error);
      }
    },

    async seveConversations(chatSet) {
      const title = chatSet.question.substring(0, 51);
      if (this.selectedTalkId == 0) {
        try {
          await this.createNewTalk(title);
          // 確保 talk 創建成功，selectedTalkId 已更新
          if (this.selectedTalkId == 0) {
            console.error("Failed to create talk. selectedTalkId is still 0.");
            throw new Error("Failed to create talk. Please try again.");
          }
        } catch (error) {
          console.error("Error creating new talk:", error);
          throw error; // 重新拋出錯誤，阻止後續操作
        }
      }

      let conversations = new TalksReferences();
      conversations.chat_id = String(this.chatId);
      conversations.talk_id = String(this.selectedTalkId);
      conversations.request_message = chatSet.question;
      conversations.response_message = chatSet.messages.join("\n");
      
      // 清理 references：轉換 resource_id 為數字，移除 null/'' 的項目
      conversations.references = chatSet.meta
        .map((item) => {
          // 轉換 resource_id 為數字（如果存在且有效）
          let resourceId = null;
          if (item.resource_id != null && item.resource_id !== '') {
            const parsed = Number(item.resource_id);
            if (!isNaN(parsed) && isFinite(parsed)) {
              resourceId = parsed;
            }
          }
          
          // 檢查是否有有效數據（至少要有 resource_id 或其他字段）
          if (resourceId == null && (!item.document_url || item.document_url === '') 
              && (!item.file_name || item.file_name === '') 
              && (!item.text || item.text === '')) {
            return null; // 過濾掉完全空的項目
          }
          
        let references = new References();
          references.document_url = item.document_url || '';
          references.file_name = item.file_name || '';
          references.resource_id = resourceId; // 現在是數字或 null
          references.text = item.text || '';
          return references;
        })
        .filter(ref => ref !== null); // 移除 null 項目

      try {
        await talksApi.createConversations(conversations);
        if (this.selectedTalkId != 0 && this.talkList[0].talk_title == "New Talk") {
          this.selectedTalkTitle = title;
          this.talkList[0].talk_title = title;
        }
      } catch (error) {
        console.error(error);
      }
    },

    makeTitle(title) {
      if (title.length > 20) {
        return title.substring(0, 20) + "..."
      }
      return title
    },

    showTimeOutModal() {
      this.isLoading = false;
      if (this.ws) { this.ws.close(); }
      let btn = document.getElementById("btnTimeOut");
      btn.click();
    },

    copyMsg(chatSet) {
      const copyText = chatSet.messages.join("\n");
      navigator.clipboard.writeText(copyText).then(() => {
        console.log("Copied to clipboard");
      }).catch(err => {
        console.error("Failed to copy: ", err);
      });
    },
    
    async updateChatModel(newModelId) {
      // 更新聊天機器人的模型
      try {
        const chat_res = await chatsApi.getById(this.chatId);
        const updateData = {
          chat_title: chat_res.data.chat_title,
          system_prompt: chat_res.data.system_prompt,
          temperature: chat_res.data.temperature,
          model: chat_res.data.model,
          icon_img: chat_res.data.icon_img,
          memory_flag: chat_res.data.memory_flag,
          groups: chat_res.data.groups?.map(g => g.id) || [],
          ai_model_id: newModelId,
        };
        await chatsApi.updateChat(this.chatId, updateData);
        
        // 更新本地模型信息
        const ai_model_res = await chatsApi.getAiModelName(newModelId);
        this.ai_model = ai_model_res.data;
        const modelName = typeof ai_model_res.data === 'string' ? ai_model_res.data : (ai_model_res.data?.model_name || '');
        this.currentModelName = modelName;
        
        // 通知父組件更新標籤（通過 window 自定義事件）
        window.dispatchEvent(new CustomEvent('model-changed', { detail: modelName }));
        
        console.log('Chat model updated to:', modelName);
      } catch (error) {
        console.error('Error updating chat model:', error);
        throw error;
      }
    },
  }
};

</script>

<template>
  <div class="chat-app">
    <!-- ローディング -->
    <LoadingIcon :is-loading="isLoading"></LoadingIcon>
    <!-- チャット履歴 -->
    <div class="container px-4">
      <div class="row gx-3">
        <div v-if="showTalkList" class="col-3">
          <div class="p-3 border bg-light rounded-3 h-100">
            <button type="button" class="btn-close mb-3 shadow" aria-label="Close" @click="showHistoryBar"></button>
            <button type="button" class="btn btn-outline-secondary w-100 mb-3 shadow"
              @click="createNewTalk(newtalkTitle)">
              {{ $t('ChatScreenView.new_talk') }}
            </button>
            <div class="his-list-left" @scroll="handleScroll">
              <div v-for="(element, index) in sortedTalkList" :key="element.id" class="px-0 d-block">
                <div class="card shadow his-card-h mb-3 w-95 card-animation" :class="{
                  'selected-talk': element.id === selectedTalkId,
                  'disabled-talk': show_messages && element.id !== selectedTalkId,
                  'pinned-talk': pinnedTalkIds[element.id]
                }"
                :draggable="pinnedTalkIds[element.id]"
                @dragstart="onDragStart($event, element.id)"
                @dragover="onDragOver($event)"
                @drop="onDrop($event, element.id)">
                  <div class="card-body font-size" @click="handleTalkClick(element.id, index)">
                    <div>{{ makeTitle(element.talk_title) }}</div>
                    <div class="pe-3 mb-3 d-flex justify-content-between align-items-center">
                      {{ getTimeString(element.created_at) }}
                      <span role="button" aria-label="toggle pin" @click.stop="togglePin(element.id)">
                        <svg v-if="!pinnedTalkIds[element.id]" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pin-angle" viewBox="0 0 16 16">
                          <path d="M9.828.722a.5.5 0 0 1 .354.146l4.95 4.95a.5.5 0 0 1 0 .707c-.48.48-1.072.588-1.503.588-.177 0-.335-.018-.46-.039l-3.134 3.134a6 6 0 0 1 .16 1.013c.046.702-.032 1.687-.72 2.375a.5.5 0 0 1-.707 0l-2.829-2.828-3.182 3.182c-.195.195-1.219.902-1.414.707s.512-1.22.707-1.414l3.182-3.182-2.828-2.829a.5.5 0 0 1 0-.707c.688-.688 1.673-.767 2.375-.72a6 6 0 0 1 1.013.16l3.134-3.133a3 3 0 0 1-.04-.461c0-.43.108-1.022.589-1.503a.5.5 0 0 1 .353-.146m.122 2.112v-.002zm0-.002v.002a.5.5 0 0 1-.122.51L6.293 6.878a.5.5 0 0 1-.511.12H5.78l-.014-.004a5 5 0 0 0-.288-.076 5 5 0 0 0-.765-.116c-.422-.028-.836.008-1.175.15l5.51 5.509c.141-.34.177-.753.149-1.175a5 5 0 0 0-.192-1.054l-.004-.013v-.001a.5.5 0 0 1 .12-.512l3.536-3.535a.5.5 0 0 1 .532-.115l.096.022c.087.017.208.034.344.034q.172.002.343-.04L9.927 2.028q-.042.172-.04.343a1.8 1.8 0 0 0 .062.46z" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pin-fill" viewBox="0 0 16 16">
                          <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A6 6 0 0 1 5 6.708V2.277a3 3 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="is_bottom" class="d-flex justify-content-center w-95">
                <div class="spinner-border text-secondary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="p-3 border bg-light rounded-3">
            <!-- ツールバー -->
            <h4>{{ makeTitle(selectedTalkTitle) }}</h4>
            <div class="d-flex align-items-center">
              <div class="form-check form-switch me-2 pe-3">
                <input class="form-check-input" type="checkbox" role="switch" id="memoryToggle" v-model="memoryFlag">
                <label class="form-check-label" for="memoryToggle">{{ $t('ChatScreenView.memory_flag') }}</label>
              </div>

              <div class="form-check form-switch me-2" v-if="showTemplateSearch">
                <input class="form-check-input" type="checkbox" role="switch" id="templateSearch"
                  v-model="templateFlag">
                <label class="form-check-label" for="templateSearch">{{ $t('ChatScreenView.template_search') }}</label>
              </div>
              <div class="form-check form-switch me-2 pe-3" v-if="showThinkingMode">
                <input class="form-check-input" type="checkbox" role="switch" id="thinkingToggle" v-model="thinkingFlag">
                <label class="form-check-label" for="thinkingToggle">Thinking Mode</label>
              </div>
              <div class="d-flex flex-row-reverse bd-highlight">
                <button class="btn btn-primary m-3 shadow" @click="showHistoryBar()">
                  {{ $t('ChatScreenView.his_talk') }}
                </button>
              </div>
            </div>

            <!-- チャット -->
            <div class="card text-bg-light mb-3 bg-white border border-white card_h shadow" ref="messageHistory">
              <div class="card-body bg-white border border-white card-scroll">
                <div v-for="(chatSet, index) in history_chats" :key="index">
                  <div class="alert  alert-info message user-message ms-auto">{{ chatSet.question }}</div>
                  <div class="d-flex flex-row bd-highlight align-items-end mb-3">
                    <div class="alert alert-secondary message chatgpt-message">
                      <span>
                        <div v-for="(line, index) in chatSet.messages" :key="index">
                          <p v-html="renderMessageLine(line)"></p>
                        </div>
                      </span>
                    </div>
                    <div>
                      <button v-if="showCopyButton" class="btn btn-primary m-3 shadow" @click="copyMsg(chatSet)">
                        {{ $t('ChatScreenView.copy') }}
                      </button>
                    </div>
                  </div>
                  <div class="text-muted small text-end mb-3">
                    {{ getMessageTimeString(chatSet.created_at) }}
                  </div>
                  <div v-if="chatSet.clarificationCandidates && chatSet.clarificationCandidates.length" class="mb-2">
                    <button
                      v-for="candidate in chatSet.clarificationCandidates"
                      :key="`candidate-history-${index}-${candidate}`"
                      type="button"
                      class="btn btn-outline-primary btn-sm me-2 mb-2"
                      @click="selectClarificationCandidate(chatSet.clarificationContext, candidate)"
                    >
                      {{ candidate }}
                    </button>
                  </div>

                  <div class="d-flex flex-row flex-wrap">
                    <div v-for="(meta, index) in getVisibleReferences(chatSet.meta)" :key="index" class="metaBtn">
                      <div class="btn-group color chatgpt-message btn-group-sm rounded-pill" role="group">
                        <button type="button" class="btn rounded-pill"
                          @click="setMetaMsg(meta.resource_id, meta.file_name, meta.text)" data-bs-toggle="modal"
                          data-bs-target="#meta"
                          style="--bs-btn-padding-y: .25rem; --bs-btn-padding-x: .5rem; --bs-btn-font-size: .75rem;">
                          {{ meta.file_name }}
                        </button>
                        <a class="btn btn-outline-primary rounded-pill" v-if="meta.document_url"
                          style="--bs-btn-padding-y: .25rem; --bs-btn-padding-x: .5rem; --bs-btn-font-size: .75rem;"
                          :href="meta.document_url" target="_blank">{{ $t('ChatScreenView.link') }}
                        </a>
                        <span v-else class="btn btn-outline-secondary rounded-pill disabled"
                          style="--bs-btn-padding-y: .25rem; --bs-btn-padding-x: .5rem; --bs-btn-font-size: .75rem;">
                          {{ $t('ChatScreenView.no_source_link') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="show_messages">
                  <div class="alert alert-info message user-message ms-auto">{{ chatSet.question }}</div>
                  <div class="alert  alert-secondary message chatgpt-message">
                    <span>
                      <div v-for="(line, index) in chatSet.messages" :key="index">
                        <p v-html="renderMessageLine(line)"></p>
                      </div>
                    </span>
                  </div>
                  <div class="text-muted small text-end mb-3">
                    {{ getMessageTimeString(chatSet.created_at) }}
                  </div>
                  <div v-if="chatSet.clarificationCandidates && chatSet.clarificationCandidates.length" class="mb-2">
                    <button
                      v-for="candidate in chatSet.clarificationCandidates"
                      :key="`candidate-current-${candidate}`"
                      type="button"
                      class="btn btn-outline-primary btn-sm me-2 mb-2"
                      @click="selectClarificationCandidate(chatSet.clarificationContext, candidate)"
                    >
                      {{ candidate }}
                    </button>
                  </div>
                  <div class="d-flex flex-row flex-wrap">
                    <div v-for="(meta, index) in getVisibleReferences(chatSet.meta)" :key="index" class="metaBtn">
                      <div class="btn-group color chatgpt-message btn-group-sm rounded-pill" role="group">
                        <button type="button" class="btn rounded-pill"
                          @click="setMetaMsg(meta.id, meta.file_name, meta.text)" data-bs-toggle="modal"
                          data-bs-target="#meta"
                          style="--bs-btn-padding-y: .25rem; --bs-btn-padding-x: .5rem; --bs-btn-font-size: .75rem;">
                          {{ meta.file_name }}
                        </button>
                        <a class="btn btn-outline-primary rounded-pill" v-if="meta.document_url"
                          style="--bs-btn-padding-y: .25rem; --bs-btn-padding-x: .5rem; --bs-btn-font-size: .75rem;"
                          :href="meta.document_url" target="_blank">{{ $t('ChatScreenView.link') }}
                        </a>
                        <span v-else class="btn btn-outline-secondary rounded-pill disabled"
                          style="--bs-btn-padding-y: .25rem; --bs-btn-padding-x: .5rem; --bs-btn-font-size: .75rem;">
                          {{ $t('ChatScreenView.no_source_link') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="showTemplate" class="d-flex flex-column">
                  <div class="ms-auto mb-3">{{ $t('ChatScreenView.template_message') }}</div>
                  <div v-for="(template, index) in chatQuestionTemplateList" :key="index"
                    class="d-flex align-items-end flex-column mb-3">
                    <button type="button" class="btn btn-outline-secondary shadow-sm rounded"
                      @click="sendTemplate(template.question_text)">
                      {{ template.question_title }}
                    </button>
                  </div>
                </div>

              </div>
              <div class="card-footer bg-white border border-white">
                <br>
                <!-- インプットフォーム -->
                <div class="input-group mb-3 shadow-sm rounded shadow">
                  <textarea class="form-control border-secondary" rows="5" v-model="userMessage"
                    :placeholder="$t('ChatScreenView.input_msg')" aria-describedby="button-send"
                    @keydown.enter.exact="keyDownEnter" @keyup.enter.exact="keyUpEnter"
                    @keydown.enter.shift="keyEnterShift" :disabled="show_messages"></textarea>
                  <button class="btn btn-outline-secondary" type="button" id="button-send" @click="sendMessage"
                    :disabled="show_messages">Send</button>
                </div>
              </div>
              <!-- エラーモーダル -->
              <div v-if="showModal" class="modal-error">
                <div class="modal-content">
                  <p>{{ errorMessage }}</p>
                  <button class="btn btn-primary" @click="closeModal">{{ $t('ChatScreenView.close') }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>



    <!-- 参考文章モーダル  -->
    <div class="modal fade" v-show="isMetaModalVisible" id="meta" tabindex="-1" data-bs-backdrop="static"
      data-bs-keyboard="false" role="dialog" aria-labelledby="modalTitleId" aria-hidden="true">
      <div class="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <a v-if="resourceId" :href="`/chats/${this.chatId}/resource/${resourceId}`" target="_blank">
              {{ modalTitle }}
            </a>
            <span v-else>{{ modalTitle }}</span>
          </div>
          <div class="modal-body">
            {{ metaModalText }}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">OK</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Button trigger modal -->
    <button type="button" class="btn btn-primary invisible" id="btnTimeOut" data-bs-toggle="modal"
      data-bs-target="#timeOutModal">
    </button>

    <!-- Time Out Modal -->
    <div class="modal fade" id="timeOutModal" tabindex="-1" aria-labelledby="exampleModalLabel"
      data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="exampleModalLabel">Time Out</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            {{ $t('ChatScreenView.error_messages') }}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">OK</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.message {
  width: fit-content;
  max-width: 80%;
}

.card_h {
  height: 70vh;
}

.card-scroll {
  overflow: auto;
}

.color {
  background-color: #D5BDFF;
}

.metaBtn {
  margin-right: 15px;
  display: inline-flex;
}

@keyframes fadeIn {
  from {
    display: none;
    opacity: 0;
    z-index: -1;
  }

  to {
    opacity: 1;
  }
}

.his-card-h {
  height: 100px;
}

.font-size {
  font-size: 15px;
}

.his-list-left {
  overflow-x: hidden;
  overflow-y: auto;
  height: 70vh;
}

.w-95 {
  width: 95%;
}

.card-animation:hover {
  cursor: pointer;
  color: white;
  background-color: gray;
}

.selected-talk {
  background-color: #007bff !important;
  color: white !important;
  border-color: #0056b3 !important;
}

.selected-talk:hover {
  background-color: #0056b3 !important;
  color: white !important;
}

.disabled-talk {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.disabled-talk:hover {
  background-color: inherit !important;
  color: inherit !important;
}

.pinned-talk {
  border-left: 4px solid #ffc107 !important;
  background-color: #fff3cd !important;
}

.pinned-talk:hover {
  background-color: #ffeaa7 !important;
}

.pinned-talk[draggable="true"] {
  cursor: move;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar:hover {
  cursor: default;
}


::-webkit-scrollbar-track {
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.06);
  -webkit-box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.08);
}

::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.12);
  -webkit-box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.2);
}
</style>